import React from 'react';
import { FiNavigation, FiPlus } from 'react-icons/fi';
import { formatDistance } from '../utils/courseDistance';
import './CourseSections.css';

/**
 * A course picker's list in sections — NEAR YOU / YOUR SOCIETY PLAYS / ALL
 * COURSES — shared by both pickers (Step1_QuickSetup, Step2_Courses) so the
 * two can't drift. The ORDER comes from `sectionCourses`
 * (utils/courseDistance.js); this only lays it out. Each picker draws its own
 * rows through `renderRow(entry)`, because one selects on a tap and the other
 * has an Add button.
 *
 * `near` describes the top section:
 * - status: 'prompt' | 'locating' | 'ready' | 'failed' | anything else (hidden)
 * - heading, control: the section's title and an optional control beside it
 * - onRequest: what "Show courses near me" does
 * - emptyText: said when a ready anchor has nothing within the radius
 * - note: an optional muted line under the section
 * - directory: { rows, miles, error, onAdd } — directory courses near the same
 *   point that the society hasn't got (course managers only; see
 *   hooks/useDirectoryNearby). Drawn here rather than by the picker: the row
 *   is the same in both, and its action — add to the society — is not the
 *   picker's own select / Add.
 *
 * Headings appear only when there is more than one kind of row to tell apart;
 * a society with no history and no location sees the plain list it always did.
 */
const SHOWN = new Set(['prompt', 'locating', 'ready', 'failed']);

const Heading = ({ children, control }) => (
    <div className="course-sections__heading">
        <span>{children}</span>
        {control}
    </div>
);

const DirectoryRows = ({ directory }) => (
    <>
        <div className="course-sections__subheading">In the course directory</div>
        {directory.rows.map((row) => (
            <div key={`dir-${row.id}`} className="course-sections__dir-row">
                <span className="course-sections__dir-name">
                    {row.name}
                    <span className="course-sections__dir-meta">
                        {formatDistance(row.km, directory.miles)} · not in your list yet
                    </span>
                </span>
                <button type="button" className="course-sections__dir-add"
                    onClick={() => directory.onAdd(row)}
                    aria-label={`Add ${row.name} to your society`}>
                    <FiPlus aria-hidden="true" /> Add to society
                </button>
            </div>
        ))}
        {directory.error && <div className="course-sections__status">{directory.error}</div>}
    </>
);

export default function CourseSections({ sections, near, renderRow }) {
    if (sections.searching) return <>{sections.rows.map(renderRow)}</>;

    const { nearby, favourites, rest } = sections;
    const status = near && SHOWN.has(near.status) ? near.status : null;
    const headed = !!status || favourites.length > 0;
    const dirRows = (near?.directory?.rows?.length || 0) > 0;

    return (
        <>
            {status === 'prompt' && (
                <button type="button" className="course-sections__ask" onClick={near.onRequest}>
                    <FiNavigation aria-hidden="true" /> Show courses near me
                </button>
            )}
            {status && status !== 'prompt' && (
                <>
                    <Heading control={near.control}>{near.heading}</Heading>
                    {status === 'locating' && (
                        <div className="course-sections__status">Finding courses near you…</div>
                    )}
                    {status === 'failed' && (
                        <div className="course-sections__status">Couldn't get your location</div>
                    )}
                    {status === 'ready' && nearby.map(renderRow)}
                    {/* "No courses within 25 miles" above a directory course
                        three miles away would contradict itself. */}
                    {status === 'ready' && !nearby.length && !dirRows && (
                        <div className="course-sections__status">{near.emptyText}</div>
                    )}
                    {status === 'ready' && dirRows && <DirectoryRows directory={near.directory} />}
                </>
            )}
            {status && near.note && <div className="course-sections__note">{near.note}</div>}

            {favourites.length > 0 && (
                <>
                    <Heading>Your society plays</Heading>
                    {favourites.map(renderRow)}
                </>
            )}
            {rest.length > 0 && (
                <>
                    {headed && <Heading>All courses</Heading>}
                    {rest.map(renderRow)}
                </>
            )}
        </>
    );
}
